import { RouteTabLink } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { Box, Divider, Paper, Tabs, Typography } from "@mui/material";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { fetchVendor } from "../../../../services/vendors";
import { VendorProfileBannerWithBadges } from "./-internal_components/VendorProfileBannerWithBadges";

const RouteComponent: React.FC = () => {
  const vendor = Route.useLoaderData();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment && /^\d+$/.test(lastSegment)) {
      navigate({ to: "/vsupplierregistration/vendor" });
    }
  }, [location.pathname, navigate]);

  const hasViewCategoryPanelPermission = true;

  const isEditRoute = location.pathname.includes("/edit");

  const handleEditVendor = (): void => {
    navigate({ to: "/vsupplierregistration/vendor/edit" });
  };

  const [selectedTab, setSelectedTab] = useState(() => {
    if (location.pathname === "/vsupplierregistration/vendor") {
      return "profile";
    }
    if (location.pathname === "/vsupplierregistration/vendor/users") {
      return "users";
    }
    if (
      location.pathname === "/vsupplierregistration/vendor/terms" ||
      location.pathname === "/vsupplierregistration/vendor/terms_exceptions"
    ) {
      return "terms";
    }
    if (location.pathname === "/vsupplierregistration/vendor/categories") {
      return "categories";
    }
    if (location.pathname === "/vsupplierregistration/vendor/documents") {
      return "documents";
    }
    return "profile";
  });

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      setSelectedTab(newValue);
    },
    [],
  );

  return (
    <Box sx={{ p: 4, width: "100%" }}>
      {!isEditRoute && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 3,
          }}
        >
          <VendorProfileBannerWithBadges
            enableEdit
            onEditVendor={handleEditVendor}
            showSapCode={false}
            vendor={vendor}
          />

          <Divider />

          <Box sx={{ gap: 1 }}>
            <Typography variant="body2">
              {vendor.address1} - {vendor.city} / {vendor.country}
            </Typography>
          </Box>
        </Paper>
      )}

      {!isEditRoute && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
          <Tabs
            aria-label="vendor details tabs"
            onChange={handleTabChange}
            value={selectedTab}
          >
            <RouteTabLink
              label={t("vsupplierregistration.vendors.details.tabs.profile")}
              to="/vsupplierregistration/vendor"
              value="profile"
            />

            <RouteTabLink
              label={t("vsupplierregistration.vendors.details.tabs.users")}
              to="/vsupplierregistration/vendor/users"
              value="users"
            />

            <RouteTabLink
              label={t("vsupplierregistration.vendors.details.tabs.terms")}
              to="/vsupplierregistration/vendor/terms"
              value="terms"
            />

            {hasViewCategoryPanelPermission && (
              <RouteTabLink
                label={t(
                  "vsupplierregistration.vendors.details.tabs.categories",
                )}
                to="/vsupplierregistration/vendor/categories"
                value="categories"
              />
            )}

            <RouteTabLink
              label={t("vsupplierregistration.vendors.details.tabs.documents")}
              to="/vsupplierregistration/vendor/documents"
              value="documents"
            />
          </Tabs>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export const Route = createFileRoute("/_private/vsupplierregistration/vendor")({
  beforeLoad({ context }) {
    const vendorId = context.auth.user?.vendor?.id;

    if (!vendorId) {
      throw new ForbiddenError();
    }

    return { vendorId };
  },
  component: RouteComponent,
  loader: ({ context: { vendorId } }) =>
    fetchVendor(parseInt(vendorId.toString(), 10)),
});
